import {
  VerticalCustom as DrillingIcon,
  UsersAltLight as RigCrewIcon,
  DrillingRigCustom as RigIcon,
  SearchLight as SearchIcon,
  SortLight,
  StarFill as StarIcon,
  StarLight as StarOutlinedIcon,
  Status as StatusIcon,
  WellClusterLightCustom as WellClusterIcon,
} from '@profgeosoft-ui/icons';
import { Button, Input, Loader, RadioButtonGroup, Scrollbar, Tooltip } from '@profgeosoft-ui/react';
import { observer } from 'mobx-react-lite';
import { type MouseEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { useService, useServiceAccessor } from 'src/packages/di';

import type { WellItemEntity } from './WellCollapse';
import type { WellListPanelEntity } from './WellListPanel.entity';
import type { WellListPanelStore } from './WellListPanel.store';
import type {
  IWellCollapseItemEntity,
  WellCollapseItemEntity,
} from '../well-collapse/components/well-collapse-item/WellCollapseItem.entity';
import type { TTabTemplateWithGSProps } from '../workspace/types';
import type { WellIndexType, WidgetStateParams } from '@go-widgets/well-logs-widget';
import type { TWellListView } from 'src/api-types/wells.types';
import type { TCreateWidgetOptions } from 'src/entities/tab/TabEntity';

import { ContextMenuButton } from '../context-menu-button/ContextMenuButton';
import { WellCollapse } from '../well-collapse/WellCollapse';

import { WellSortingTooltip } from './WellSortingTooltip';

import styles from './WellListPanel.module.scss';

const groupingIcons: Partial<Record<string, React.ReactElement>> = {
  'rig-icon': <RigIcon className={styles.groupTypeIcon} />,
  'rig-crew-icon': <RigCrewIcon className={styles.groupTypeIcon} />,
  'status-icon': <StatusIcon className={styles.groupTypeIcon} />,
  'location-icon': <WellClusterIcon className={styles.groupTypeIcon} />,
};

type Props = {
  wellListEntity: WellListPanelEntity;
  store: WellListPanelStore;
  refAt?: <U extends HTMLDivElement>(key: number) => Ref<U>;
  onCreateWellLogsWidget?(
    wellIndexType: WellIndexType,
    wellId: number | null,
    stateParams: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions
  ): void;
  onCreateOperationalParamsWidget?(wellIndexType: WellIndexType, wellId: number | null, enableSound: boolean): void;
  onCreateWellAnalyseWidget?(wellId: number | null, stateParams?: Partial<WidgetStateParams> | null): void;
  onCreateDirectionalDrillingWidget?(wellId: number | null): void;
  onCreateImageBroadcastWidget?(wellId: number | null): void;
  onCreateVideoBroadcastWidget?(wellId: number | null): void;
  onCreateDayDepthChartWidget?(wellId: number | null): void;
  onCreateHeatmapWidget?(wellId: number | null): void;
  onCreateTemplate?(template: TTabTemplateWithGSProps, wellId: number | null, customTabName?: string): void;
  onWellSelect?(entity: WellCollapseItemEntity): void;
};

export const WellListPanelBody = observer(function WellListPanelBody({
  store,
  wellListEntity,
  refAt,
  onCreateWellLogsWidget,
  onCreateOperationalParamsWidget,
  onCreateWellAnalyseWidget,
  onCreateDirectionalDrillingWidget,
  onCreateImageBroadcastWidget,
  onCreateVideoBroadcastWidget,
  onCreateDayDepthChartWidget,
  onCreateHeatmapWidget,
  onCreateTemplate,
  onWellSelect,
}: Props) {
  const { t } = useTranslation();

  const { theme } = useService('theme');
  const {
    changeFilterBy,
    changeGroupBy,
    isLoading,
    changeSearchValue,
    selectWell,
    toggleFavorite,
    favoritesWells,
    onCollapseKeysChange,
    activeKey,
    changeSorting,
  } = store;

  const {
    filterBy,
    groupBy,
    searchValue,
    selectedWellID,
    wells,
    sorting,
    view: { item: itemView },
  } = wellListEntity;

  const getDataService = useServiceAccessor('preloadService');

  const handleWellSelect = (entity: WellCollapseItemEntity): void => {
    if (entity.id === wellListEntity.selectedWellID) {
      return;
    }

    selectWell(entity);
    onWellSelect?.(entity);
  };

  const dataService = getDataService();
  const wellListView = dataService.getPreloadedData<TWellListView>('well-list-control');

  const filteringItems = wellListView.filtering.filters.map((item, index) => ({
    label: t(`Labels:${item.name}.label`, { defaultValue: item.name }),
    value: index,
  }));

  const groupingItems = wellListView.grouping.groups.map((group, index) => {
    const { icon, name, fieldId } = group;

    return {
      label: groupingIcons[icon] ? (
        <Tooltip title={t(`Labels:${fieldId}.label`)} children={groupingIcons[icon]} />
      ) : (
        name
      ),
      value: index,
    };
  });

  const _onFavoriteToggle = (e: MouseEvent, item: IWellCollapseItemEntity) => {
    e.stopPropagation();
    toggleFavorite(item);
  };

  const renderButtons = (item: IWellCollapseItemEntity) => (
    <>
      <Button
        icon={
          favoritesWells.hasWell(item) ? (
            <StarIcon className={styles.activeIcon} />
          ) : (
            <StarOutlinedIcon className={styles.icon} />
          )
        }
        variant="flat"
        onClick={(e) => _onFavoriteToggle(e, item)}
        className={styles.iconButton}
      />
      <ContextMenuButton
        id={item.id}
        onCreateWellLogsWidget={onCreateWellLogsWidget}
        onCreateTemplate={(template, wellId) => onCreateTemplate?.(template, wellId, item.name)}
        onCreateOperationalParamsWidget={onCreateOperationalParamsWidget}
        onCreateWellAnalyseWidget={onCreateWellAnalyseWidget}
        onCreateDirectionalDrillingWidget={onCreateDirectionalDrillingWidget}
        onCreateImageBroadcastWidget={onCreateImageBroadcastWidget}
        onCreateVideoBroadcastWidget={onCreateVideoBroadcastWidget}
        onCreateDayDepthChartWidget={onCreateDayDepthChartWidget}
        onCreateHeatmapWidget={onCreateHeatmapWidget}
      />
    </>
  );

  return (
    <div className={styles.wrapper}>
      {isLoading && <Loader className={styles.loader} />}
      <div className={styles.contentWrapper}>
        <RadioButtonGroup
          variant="flat"
          items={filteringItems}
          value={filterBy}
          className={styles.switchContainer}
          itemClassName={styles.switchButton}
          onChange={changeFilterBy}
        />
        <div className={styles.controlsWrapper}>
          <Input
            allowClear
            prefix={<SearchIcon className={styles.searchIcon} />}
            value={searchValue}
            inputClassName={styles.searchInput}
            placeholder={t('wellList:search')}
            onChange={(e) => changeSearchValue(e.target.value)}
          />
          <Tooltip
            placement="right"
            trigger={['click']}
            title={<WellSortingTooltip sorting={sorting} changeSorting={changeSorting} view={wellListView} />}
          >
            <Button icon={<SortLight className={styles.sortIcon} />} className={styles.sortButton} />
          </Tooltip>
        </div>
        <Scrollbar>
          <WellCollapse<WellItemEntity>
            renderIcon={(item) => {
              const color =
                item.status === 'COMPLETED' && theme === 'light'
                  ? 'rgba(64, 81, 134, 1)'
                  : itemView.statusColor[item.status];
              return <DrillingIcon className={styles.drillingIcon} style={{ color: color ?? '#ffffff80' }} />;
            }}
            renderButtons={renderButtons}
            collapses={wells}
            onItemSelect={handleWellSelect}
            refAt={refAt}
            activeId={selectedWellID ?? void 0}
            activeKey={activeKey}
            onChange={onCollapseKeysChange}
          />
        </Scrollbar>
      </div>
      <RadioButtonGroup
        variant="flat"
        items={groupingItems}
        value={groupBy}
        itemClassName={styles.groupTypeButton}
        className={styles.groupTypeContainer}
        onChange={changeGroupBy}
      />
    </div>
  );
});
